import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { FaUser, FaEnvelope, FaLock, FaSave, FaTimes, FaEdit, FaGraduationCap, FaClipboardList, FaTrophy, FaClock, FaChartBar, FaHistory, FaUsers, FaArrowRight, FaEye, FaChevronDown, FaCheck, FaCheckCircle, FaUpload } from 'react-icons/fa';
import { getApiBaseUrl, StatsAPI, QuizzesAPI } from '../utils/api';
import { getToken } from '../utils/auth';
import { toast } from 'react-hot-toast';
import SpinnerLoading from '../components/SpinnerLoading';
import ContributionGraph from '../components/ContributionGraph';

interface UserProfile {
    id: string;
    email: string;
    name: string;
    phone?: string;
    dob?: string;
    gender?: 'male' | 'female' | 'other';
    createdAt: string;
    lastLoginAt?: string;
    passwordChangedAt?: string;
    avatar?: string;
}

interface UserStats {
    classesOwned: number;
    quizzesOwned: number;
    quizzesTaken: number;
    totalSessions: number;
    averageScore: number;
    recentSessions: RecentSession[];
    activityLog?: Record<string, number>;
}

interface RecentSession {
    id: string;
    quizId: string;
    quizTitle: string;
    className: string;
    score: number;
    totalQuestions: number;
    percentage: number;
    completedAt: string;
}

const ProfilePage: React.FC = () => {
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [stats, setStats] = useState<UserStats | null>(null);
    const [activeTab, setActiveTab] = useState<'overview' | 'history' | 'management'>('overview');

    // Stats Management States
    const [myClasses, setMyClasses] = useState<any[]>([]);
    const [selectedClassId, setSelectedClassId] = useState<string>('');
    const [classQuizzes, setClassQuizzes] = useState<any[]>([]);
    const [selectedQuizId, setSelectedQuizId] = useState<string>('');
    const [quizDetails, setQuizDetails] = useState<any>(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    // Dropdown States
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);

    // Edit states
    const [editingInfo, setEditingInfo] = useState(false);
    const [editingEmail, setEditingEmail] = useState(false);
    const [editingPassword, setEditingPassword] = useState(false);

    // Form states
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        dob: '',
        gender: 'other' as 'male' | 'female' | 'other'
    });

    const [newEmail, setNewEmail] = useState('');
    const [emailPassword, setEmailPassword] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // Loading states for each action
    const [savingInfo, setSavingInfo] = useState(false);
    const [savingEmail, setSavingEmail] = useState(false);
    const [savingPassword, setSavingPassword] = useState(false);
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Password visibility states
    const [showCurrentPass, setShowCurrentPass] = useState(false);
    const [showNewPass, setShowNewPass] = useState(false);
    const [showConfirmPass, setShowConfirmPass] = useState(false);

    const API_URL = getApiBaseUrl();

    useEffect(() => {
        loadProfileData();
        loadMyClasses(); // Now uses /classes?mine=true
    }, []);

    const isRestoringRef = useRef(false);

    useEffect(() => {
        if (selectedClassId) {
            loadClassQuizzes(selectedClassId);
            // Only reset quiz selection if not restoring from navigation
            if (!isRestoringRef.current) {
                setSelectedQuizId('');
                setQuizDetails(null);
            }
        }
    }, [selectedClassId]);

    // Restore activeTab and management state from navigation (e.g., when navigating back from ResultsPage)
    useEffect(() => {
        // Set restoration flag if we have state to restore
        if (location.state?.selectedClassId || location.state?.selectedQuizId) {
            isRestoringRef.current = true;
        }

        if (location.state?.activeTab) {
            setActiveTab(location.state.activeTab);
        }
        // Restore management tab selections if returning from stats view
        if (location.state?.selectedClassId) {
            setSelectedClassId(location.state.selectedClassId);
        }
        if (location.state?.selectedQuizId) {
            setSelectedQuizId(location.state.selectedQuizId);
        }

        // Reset restoration flag after a brief delay to allow effects to run
        if (isRestoringRef.current) {
            setTimeout(() => {
                isRestoringRef.current = false;
            }, 100);
        }
    }, [location.state]);

    // Track last loaded quiz to prevent infinite reload
    const lastLoadedQuizRef = useRef<string>('');

    // Auto-load quiz stats when quiz is selected and changed
    useEffect(() => {
        if (selectedQuizId && activeTab === 'management' && selectedQuizId !== lastLoadedQuizRef.current) {
            lastLoadedQuizRef.current = selectedQuizId;
            handleLoadQuizStats();
        }
    }, [selectedQuizId, activeTab]);

    // Click outside to close dropdowns
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Element;
            if (!target.closest('.custom-dropdown-container')) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const loadProfileData = async () => {
        try {
            setLoading(true);
            const token = getToken();
            if (!token) {
                navigate('/login');
                return;
            }

            // Try /auth/me first (most reliable)
            try {
                const meRes = await fetch(`${API_URL}/auth/me`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (meRes.ok) {
                    const userData = await meRes.json();

                    // Handle avatar URL (same logic as Sidebar)
                    let avatarUrl = null;
                    if (userData.user.avatar) {
                        const baseUrl = API_URL.split('/api')[0];
                        avatarUrl = userData.user.avatar.startsWith('http')
                            ? userData.user.avatar
                            : `${baseUrl}${userData.user.avatar}`;
                    }

                    setProfile({
                        id: userData.user.id,
                        email: userData.user.email,
                        name: userData.user.name || userData.user.email.split('@')[0],
                        phone: userData.user.phone,
                        dob: userData.user.dob,
                        gender: userData.user.gender,
                        avatar: avatarUrl,
                        createdAt: userData.user.created_at || new Date().toISOString()
                    });
                    setFormData({
                        name: userData.user.name || userData.user.email.split('@')[0],
                        phone: userData.user.phone || '',
                        dob: userData.user.dob || '',
                        gender: (userData.user.gender as any) || 'other'
                    });
                    setNewEmail(userData.user.email);
                } else {
                    setProfile({ id: '', email: '', name: 'User', createdAt: new Date().toISOString() });
                }
            } catch {
                setProfile({ id: '', email: '', name: 'User', createdAt: new Date().toISOString() });
            }

            // Try to get stats (optional, fail silently)
            try {
                const statsRes = await fetch(`${API_URL}/profile/stats`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (statsRes.ok) {
                    const statsData = await statsRes.json();
                    setStats(statsData);
                } else {
                    setStats({ classesOwned: 0, quizzesOwned: 0, quizzesTaken: 0, totalSessions: 0, averageScore: 0, recentSessions: [] });
                }
            } catch {
                setStats({ classesOwned: 0, quizzesOwned: 0, quizzesTaken: 0, totalSessions: 0, averageScore: 0, recentSessions: [] });
            }
        } catch (error) {
            console.error('Error loading profile:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadMyClasses = async () => {
        try {
            const token = getToken();
            if (!token) return;

            // Use /classes?mine=true instead of stats endpoint
            const res = await fetch(`${API_URL}/classes?mine=true`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.ok) {
                const data = await res.json();
                setMyClasses(data);
            } else {
                setMyClasses([]);
            }
        } catch (error) {
            setMyClasses([]);
        }
    };

    const loadClassQuizzes = async (classId: string) => {
        try {
            const token = getToken();
            if (!token) return;

            const data = await QuizzesAPI.byClass(classId, token);
            if (data) {
                setClassQuizzes(data);
            } else {
                setClassQuizzes([]);
            }
        } catch (error) {
            setClassQuizzes([]);
        }
    };

    const handleLoadQuizStats = async () => {
        if (!selectedQuizId) return;
        try {
            setLoadingStats(true);
            const token = getToken();
            if (!token) return;
            const res = await StatsAPI.getQuizStats(selectedQuizId, token);
            setQuizDetails(res);
        } catch (error) {
            toast.error('Lỗi tải thống kê quiz');
        } finally {
            setLoadingStats(false);
        }
    };

    const handleUpdateInfo = async () => {
        if (!formData.name.trim() || formData.name.trim().length < 2) {
            toast.error('Tên phải có ít nhất 2 ký tự');
            return;
        }
        try {
            setSavingInfo(true);
            const token = getToken();
            const res = await fetch(`${API_URL}/profile/info`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(formData)
            });

            if (!res.ok) throw new Error();

            const data = await res.json();
            toast.success('Cập nhật thông tin thành công!');

            setProfile(prev => prev ? {
                ...prev,
                name: formData.name,
                phone: formData.phone,
                dob: formData.dob,
                gender: formData.gender
            } : null);

            setEditingInfo(false);
            window.dispatchEvent(new Event('authChange'));
        } catch {
            toast.error('Không thể cập nhật thông tin');
        } finally {
            setSavingInfo(false);
        }
    };

    const handleUpdateEmail = async () => {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
            toast.error('Email không hợp lệ');
            return;
        }
        if (!emailPassword) {
            toast.error('Vui lòng nhập mật khẩu hiện tại');
            return;
        }
        try {
            setSavingEmail(true);
            const token = getToken();
            const res = await fetch(`${API_URL}/profile/email`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ email: newEmail, password: emailPassword })
            });
            if (!res.ok) throw new Error();
            toast.success('Cập nhật email thành công!');
            setProfile(prev => prev ? { ...prev, email: newEmail } : null);
            setEditingEmail(false);
            setEmailPassword('');
        } catch {
            toast.error('Không thể cập nhật email');
        } finally {
            setSavingEmail(false);
        }
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Client-side validation
        if (!file.type.startsWith('image/')) {
            toast.error('Chỉ chấp nhận file ảnh (JPG, PNG, GIF)');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Kích thước ảnh tối đa 5MB');
            return;
        }

        try {
            setUploadingAvatar(true);
            const token = getToken();
            const formData = new FormData();
            formData.append('avatar', file);

            const res = await fetch(`${API_URL}/profile/avatar`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.message || 'Upload failed');
            }
            const data = await res.json();
            toast.success('Cập nhật avatar thành công!');

            // Update profile with new avatar URL
            // Backend now returns: /api/public/uploads/avatars/avatar_123.jpg
            // For production (ictu.site): https://ictu.site/api/public/uploads/avatars/...
            // For localhost: http://localhost/quiz-backend-php/api/public/uploads/avatars/...
            const baseUrl = API_URL.split('/api')[0]; // Get base domain
            const avatarUrl = data.avatar.startsWith('http') ? data.avatar : `${baseUrl}${data.avatar}`;

            setProfile(prev => prev ? { ...prev, avatar: avatarUrl } : null);

            // Force reload to ensure persistence visuals
            window.dispatchEvent(new Event('authChange'));

        } catch (error: any) {
            console.error('Avatar upload error:', error);
            toast.error(error.message || 'Không thể upload ảnh');
        } finally {
            setUploadingAvatar(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleChangePassword = async () => {
        if (!currentPassword || !newPassword || !confirmPassword) {
            toast.error('Vui lòng nhập đầy đủ thông tin');
            return;
        }
        if (newPassword.length < 6) {
            toast.error('Mật khẩu mới phải có ít nhất 6 ký tự');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('Mật khẩu xác nhận không khớp');
            return;
        }
        try {
            setSavingPassword(true);
            const token = getToken();
            const res = await fetch(`${API_URL}/profile/password`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ currentPassword, newPassword })
            });
            if (!res.ok) throw new Error();
            toast.success('Đổi mật khẩu thành công!');
            setEditingPassword(false);
            setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
            loadProfileData();
        } catch {
            toast.error('Không thể đổi mật khẩu');
        } finally {
            setSavingPassword(false);
        }
    };

    const formatDateTime = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('vi-VN', {
            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center dark:bg-gray-900">
            <div style={{ transform: 'scale(0.435)' }}><SpinnerLoading /></div>
        </div>
    );

    if (!profile || !stats) return <div className="text-center p-10">Error loading profile</div>;

    const selectedClass = myClasses.find(c => c.id === selectedClassId);
    const selectedQuiz = classQuizzes.find(q => q.id === selectedQuizId);

    return (
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 animate-fadeIn">
            {/* Hero Section & Stats - Redesigned */}
            <div className="mb-6 lg:mb-10 animate-slideDownIn">
                <div className="relative rounded-3xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 sm:p-10 shadow-2xl overflow-hidden">
                    {/* Background Glow */}
                    <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>

                    <div className="relative z-10 flex flex-col xl:flex-row items-center justify-between gap-10">
                        {/* User Info */}
                        <div className="flex flex-col sm:flex-row items-center gap-8">
                            <div className="relative">
                                {/* Avatar */}
                                <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full p-1 bg-gradient-to-br from-blue-500 to-purple-500">
                                    <div className="w-full h-full rounded-full bg-gray-100 dark:bg-gray-700 p-1">
                                        <div className="w-full h-full rounded-full overflow-hidden bg-gray-800 flex items-center justify-center text-4xl font-bold text-gray-500">
                                            {profile.avatar ? (
                                                <img src={profile.avatar} alt={profile.name} className="w-full h-full object-cover" />
                                            ) : (
                                                profile.name ? profile.name.charAt(0).toUpperCase() : 'U'
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="text-center sm:text-left space-y-2 sm:space-y-3">
                                <h1 className="text-2xl sm:text-4xl font-bold text-yellow-500 tracking-tight" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                                    {profile.name || 'User'}
                                </h1>
                                <div className="inline-flex">
                                    <span className="px-3 py-1 rounded-lg bg-gray-100 dark:bg-gray-700 text-blue-400 text-sm font-medium border border-blue-500/20 flex items-center gap-2">
                                        <FaEnvelope className="text-xs" /> {profile.email}
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-3 text-xs font-mono font-medium text-green-500">
                                    <span className="flex items-center gap-1.5 bg-green-50 dark:bg-green-900/20 px-3 py-1 rounded-full border border-green-200 dark:border-green-800/30">
                                        <FaClock className="text-green-500" /> Hoạt động: {new Date(profile.createdAt).toLocaleString('en-GB').replace(',', '')}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Stats Grid - Dark Cards */}
                        <div className="grid grid-cols-2 gap-4 w-full xl:w-auto">
                            {[
                                { label: 'Lớp học', value: stats.classesOwned, icon: FaGraduationCap },
                                { label: 'Quiz đã tạo', value: stats.quizzesOwned, icon: FaClipboardList },
                                { label: 'Đã hoàn thành', value: stats.quizzesTaken, icon: FaCheckCircle },
                                { label: 'Điểm trung bình', value: `${stats.averageScore}%`, icon: FaTrophy }
                            ].map((item, idx) => (
                                <div key={idx} className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors rounded-xl p-4 flex items-center gap-4 border border-gray-700/50 group cursor-default">
                                    <div className="text-gray-400 group-hover:text-white transition-colors text-2xl">
                                        <item.icon />
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase font-bold text-gray-500 mb-0.5">{item.label}</div>
                                        <div className="text-xl font-bold text-gray-900 dark:text-white">{item.value}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Tabs - Clean Segmented Style */}
            <div className="flex justify-center mb-10 px-4">
                <div className="flex flex-wrap justify-center p-1 gap-1 bg-gray-100 dark:bg-gray-800/50 rounded-2xl">
                    {[
                        { id: 'overview', label: 'Tổng quan', icon: FaUser },
                        { id: 'history', label: 'Lịch sử', icon: FaHistory },
                        { id: 'management', label: 'Thống kê', icon: FaChartBar }
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as any)}
                            className={`
                                relative flex items-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-6 sm:py-3 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-300
                                ${activeTab === tab.id
                                    ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-lg shadow-blue-500/10'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-gray-700/30'
                                }
                            `}
                        >
                            <tab.icon className="text-base" />
                            <span>{tab.label}</span>
                            {activeTab === tab.id && (
                                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-blue-600 dark:bg-blue-400 rounded-full" />
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content Area */}
            <style>{`
                /* Hide default password toggle in Edge/IE */
                input::-ms-reveal,
                input::-ms-clear {
                    display: none;
                }
                /* Gradient row dividers */
                .gradient-row-divider::after {
                    content: '';
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 1px;
                    background: linear-gradient(to right, transparent, rgb(229, 231, 235), transparent);
                }
                .dark .gradient-row-divider::after {
                    background: linear-gradient(to right, transparent, rgba(55, 65, 81, 0.5), transparent);
                }
                /* Smooth tab content transitions */
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .tab-content {
                    animation: fadeIn 0.3s ease-out;
                }
            `}</style>
            <div className="min-h-[500px]">
                {activeTab === 'overview' && (
                    <div className="space-y-8 tab-content">
                        {/* Settings Form */}
                        {/* Settings Form - Premium Redesign */}
                        {/* Settings Form - Redesigned to Split Layout */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-5 sm:p-8 border border-gray-200 dark:border-gray-700">
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-6 sm:mb-8">
                                <FaUser className="text-blue-500" />
                                Thông tin cá nhân
                            </h2>

                            <div className="flex flex-col md:flex-row gap-6 md:gap-10">
                                {/* Left: Avatar Column */}
                                <div className="w-full md:w-56 flex flex-col items-center gap-5">
                                    <div className="relative w-32 h-32 md:w-40 md:h-40 group">
                                        <div className="w-full h-full rounded-full border-4 border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-900 shadow-xl">
                                            {profile.avatar ? (
                                                <img src={profile.avatar} alt={profile.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-4xl text-gray-600 font-bold">
                                                    {profile.name ? profile.name.charAt(0).toUpperCase() : 'U'}
                                                </div>
                                            )}
                                        </div>
                                        {/* Upload Overlay */}
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleFileChange}
                                            accept="image/jpeg,image/png,image/gif,image/webp"
                                            className="hidden"
                                        />
                                    </div>
                                    <div className="flex gap-2 w-full justify-center">
                                        <button
                                            onClick={() => profile.avatar && window.open(profile.avatar, '_blank')}
                                            className="p-2.5 bg-gray-100 dark:bg-gray-700 text-blue-500 rounded-lg hover:bg-blue-600 hover:text-white transition-all shadow-lg flex items-center justify-center"
                                            title="Xem ảnh lớn"
                                        >
                                            <FaEye />
                                        </button>
                                        <button
                                            onClick={() => fileInputRef.current?.click()}
                                            className="p-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-lg flex items-center justify-center"
                                            title="Đổi ảnh đại diện"
                                        >
                                            <FaUpload />
                                        </button>
                                    </div>
                                </div>

                                {/* Right: Info List */}
                                <div className="flex-1 flex flex-col gap-6">
                                    <div className="border-b border-gray-100 dark:border-gray-700 pb-4">
                                        <div className="flex justify-between items-center mb-4">
                                            <div className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">THÔNG TIN CÁ NHÂN</div>
                                            {!editingInfo && (
                                                <button onClick={() => setEditingInfo(true)} className="text-blue-500 font-bold text-xs flex items-center gap-1 hover:text-blue-400 transition-colors">
                                                    <FaEdit /> <span className="hidden sm:inline">Chỉnh sửa</span>
                                                </button>
                                            )}
                                        </div>

                                        <div className="space-y-4">
                                            {/* Name */}
                                            <div>
                                                <label className="text-xs text-gray-400 block mb-1">Họ và tên</label>
                                                {editingInfo ? (
                                                    <input
                                                        value={formData.name}
                                                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                                                        className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-3 py-2 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-blue-500"
                                                        placeholder="Nhập họ tên..."
                                                    />
                                                ) : (
                                                    <div className="text-base font-bold text-gray-900 dark:text-white">{profile.name}</div>
                                                )}
                                            </div>

                                            {/* Phone & Email Grid */}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                {/* Phone */}
                                                <div>
                                                    <label className="text-xs text-gray-400 block mb-1">Số điện thoại</label>
                                                    {editingInfo ? (
                                                        <input
                                                            value={formData.phone}
                                                            onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                                            className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-3 py-2 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-blue-500"
                                                            placeholder="Nhập số điện thoại..."
                                                        />
                                                    ) : (
                                                        <div className="text-sm font-medium text-gray-900 dark:text-white">{profile.phone || 'Chưa cập nhật'}</div>
                                                    )}
                                                </div>

                                                {/* Email */}
                                                <div>
                                                    <label className="text-xs text-gray-400 block mb-1">Email</label>
                                                    <div className="flex flex-wrap items-center gap-2 min-h-[38px]">
                                                        <div className="text-sm font-medium text-gray-900 dark:text-white break-all">{profile.email}</div>
                                                        <span className="bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400 text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider flex items-center gap-1 whitespace-nowrap shrink-0">
                                                            <FaCheckCircle className="text-[10px]" /> VERIFIED
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                {/* DOB */}
                                                <div>
                                                    <label className="text-xs text-gray-400 block mb-1">Ngày sinh</label>
                                                    {editingInfo ? (
                                                        <input
                                                            type="date"
                                                            value={formData.dob}
                                                            onChange={e => setFormData({ ...formData, dob: e.target.value })}
                                                            className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-3 py-2 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-blue-500"
                                                        />
                                                    ) : (
                                                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                            {profile.dob ? new Date(profile.dob).toLocaleDateString('vi-VN') : 'Chưa cập nhật'}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Gender */}
                                                <div>
                                                    <label className="text-xs text-gray-400 block mb-1">Giới tính</label>
                                                    {editingInfo ? (
                                                        <select
                                                            value={formData.gender}
                                                            onChange={e => setFormData({ ...formData, gender: e.target.value as any })}
                                                            className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-3 py-2 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-blue-500"
                                                        >
                                                            <option value="male">Nam</option>
                                                            <option value="female">Nữ</option>
                                                            <option value="other">Khác</option>
                                                        </select>
                                                    ) : (
                                                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                            {profile.gender === 'male' ? 'Nam' : profile.gender === 'female' ? 'Nữ' : 'Khác'}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Action Buttons */}
                                            {editingInfo && (
                                                <div className="flex gap-2 pt-2">
                                                    <button
                                                        onClick={handleUpdateInfo}
                                                        disabled={savingInfo}
                                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold shadow hover:bg-blue-700 transition-colors disabled:opacity-50"
                                                    >
                                                        {savingInfo ? 'Đang lưu...' : 'Lưu thay đổi'}
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setEditingInfo(false);
                                                            setFormData({
                                                                name: profile.name,
                                                                phone: profile.phone || '',
                                                                dob: profile.dob || '',
                                                                gender: profile.gender as any || 'other'
                                                            });
                                                        }}
                                                        className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-bold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                                                    >
                                                        Hủy
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Password Row */}
                                    <div className="border-b border-gray-100 dark:border-gray-700 pb-4">
                                        <div className="flex justify-between items-center mb-2">
                                            <div className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">MẬT KHẨU</div>
                                            {!editingPassword && (
                                                <button onClick={() => setEditingPassword(true)} className="text-blue-500 font-bold text-xs flex items-center gap-1 hover:text-blue-400 transition-colors">
                                                    <FaEdit /> <span className="hidden sm:inline">Chỉnh sửa</span>
                                                </button>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            {editingPassword ? (
                                                <div className="space-y-3 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                                    <input type="password" placeholder="Mật khẩu cũ" className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded p-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-blue-500" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} />
                                                    <input type="password" placeholder="Mật khẩu mới" className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded p-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-blue-500" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                                                    <input type="password" placeholder="Nhập lại mật khẩu mới" className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded p-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-blue-500" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
                                                    <div className="flex gap-2 justify-end mt-2">
                                                        <button onClick={handleChangePassword} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold shadow-lg shadow-blue-500/30 hover:bg-blue-700 transition-colors">Lưu thay đổi</button>
                                                        <button onClick={() => { setEditingPassword(false); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }} className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-bold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">Hủy</button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-lg font-bold text-gray-500 tracking-widest">••••••••</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="text-right text-xs text-gray-600 mt-2 font-mono">
                                        Cập nhật lần cuối: {profile.passwordChangedAt ? formatDateTime(profile.passwordChangedAt) : 'Chưa cập nhật'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Contribution Graph Section - Added */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 sm:p-8 animate-fadeIn border border-gray-200 dark:border-gray-700">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                        <FaChartBar className="text-blue-500" /> Hoạt động làm bài
                                    </h2>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Biểu đồ thể hiện số bài kiểm tra bạn đã hoàn thành theo năm</p>
                                </div>
                                <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1 border border-gray-200 dark:border-gray-600">
                                    {[2026, 2025].map(year => (
                                        <button
                                            key={year}
                                            onClick={() => setSelectedYear(year)}
                                            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${selectedYear === year ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-200'}`}
                                        >
                                            {year}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="w-full bg-white dark:bg-[#0d1117] rounded-xl p-4 border border-gray-200 dark:border-[#30363d] overflow-x-auto custom-scrollbar">
                                <ContributionGraph data={stats?.activityLog || {}} year={selectedYear} />
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'history' && (
                    <div className="space-y-6 tab-content">
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 sm:p-8">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                                <FaHistory className="text-blue-500" /> Lịch sử làm bài
                            </h2>

                            {(stats?.recentSessions?.length || 0) === 0 ? (
                                <div className="text-center py-12 bg-gray-50 dark:bg-gray-700/30 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700">
                                    <FaClock className="mx-auto text-4xl text-gray-300 dark:text-gray-600 mb-4" />
                                    <p className="text-gray-500 dark:text-gray-400 font-medium">Bạn chưa thực hiện bài kiểm tra nào.</p>
                                </div>
                            ) : (
                                <div className="grid gap-5 max-h-[65vh] overflow-y-auto custom-scrollbar pr-2 py-4">
                                    {(stats?.recentSessions || []).map(session => (
                                        <div key={session.id} className="group relative bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 border-l-4 border-l-gray-300 dark:border-l-gray-600 hover:border-l-primary-500 dark:hover:border-l-primary-500 rounded-2xl p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 hover:border-blue-200 dark:hover:border-blue-900/50 flex flex-col md:flex-row md:items-center justify-between gap-6 overflow-hidden">
                                            {/* Decorative side bar - REMOVED since border-l-4 replaces it */}

                                            <div className="flex-1 z-10">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <span className="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 px-2.5 py-1 rounded-lg text-xs font-medium tracking-wider flex items-center gap-1.5">
                                                        <FaGraduationCap /> {session.className}
                                                    </span>
                                                    <span className="text-gray-300">•</span>
                                                    <span className="text-gray-400 text-xs font-medium flex items-center gap-1">
                                                        <FaClock className="text-[10px]" /> {formatDateTime(session.completedAt)}
                                                    </span>
                                                </div>
                                                <h3 className="font-medium text-lg dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                                    {session.quizTitle}
                                                </h3>
                                            </div>

                                            <div className="flex items-center justify-between md:justify-end gap-8 w-full md:w-auto mt-2 md:mt-0 pt-4 md:pt-0 border-t md:border-0 border-gray-100 dark:border-gray-700 z-10">
                                                <div className="text-center group-hover:scale-105 transition-transform duration-300">
                                                    <span className="block text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-1">Điểm số</span>
                                                    <span className="font-semibold text-2xl text-gray-900 dark:text-white">
                                                        {session.score}<span className="text-gray-400 text-sm">/{session.totalQuestions}</span>
                                                    </span>
                                                </div>

                                                <div className="text-center min-w-[60px]">
                                                    <div className={`
                                                        w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm shadow-inner
                                                        ${session.percentage >= 80 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 ring-2 ring-green-500/10' :
                                                            session.percentage >= 50 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 ring-2 ring-yellow-500/10' :
                                                                'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 ring-2 ring-red-500/10'
                                                        }
                                                    `}>
                                                        {session.percentage}%
                                                    </div>
                                                </div>

                                                <button
                                                    onClick={() => navigate(`/results/${session.quizId}`, { state: { sessionId: session.id, fromProfile: true, activeTab: 'history' } })}
                                                    className="w-10 h-10 rounded-full bg-gray-50 dark:bg-gray-700 hover:bg-blue-600 hover:text-white dark:hover:bg-blue-500 text-gray-400 flex items-center justify-center transition-all duration-300 shadow-sm hover:shadow-lg"
                                                    title="Xem chi tiết"
                                                >
                                                    <FaArrowRight />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'management' && (
                    <div className="space-y-8 tab-content">
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl">
                            <div className="p-8 sm:p-10">
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-2">
                                    <FaChartBar className="text-blue-500" />
                                    Thống kê chi tiết
                                </h2>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 italic">Xem chi tiết kết quả và phân tích bài làm của học viên</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Class Custom Dropdown */}
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-wide">Chọn Lớp học</label>
                                        <div className="relative custom-dropdown-container">
                                            <button
                                                className={`
                                                    w-full text-left appearance-none border border-gray-200 dark:border-gray-700 
                                                    rounded-xl px-4 py-3 bg-white dark:bg-gray-800 
                                                    focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 
                                                    transition-all duration-200 flex justify-between items-center shadow-sm hover:shadow-md
                                                    ${openDropdown === 'class' ? 'ring-2 ring-blue-500/50 border-blue-500' : ''}
                                                `}
                                                onClick={() => setOpenDropdown(openDropdown === 'class' ? null : 'class')}
                                            >
                                                <span className={`flex-1 text-left font-medium ${selectedClass ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>
                                                    {selectedClass ? selectedClass.name : '-- Chọn lớp học --'}
                                                </span>
                                                <FaChevronDown className={`text-xs text-gray-400 transition-transform duration-300 ${openDropdown === 'class' ? 'rotate-180 text-blue-500' : ''}`} />
                                            </button>

                                            {openDropdown === 'class' && (
                                                <div className="absolute top-full left-0 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-20 overflow-hidden animate-slideUp text-left">
                                                    <div className="max-h-60 overflow-y-auto custom-scrollbar">
                                                        {myClasses.length === 0 ? (
                                                            <div className="px-4 py-3 text-gray-500 text-sm">Bạn chưa có lớp học nào</div>
                                                        ) : (
                                                            myClasses.map(c => (
                                                                <button
                                                                    key={c.id}
                                                                    onClick={() => {
                                                                        setSelectedClassId(c.id);
                                                                        setOpenDropdown(null);
                                                                    }}
                                                                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${selectedClassId === c.id ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-700 dark:text-gray-300'}`}
                                                                >
                                                                    <span className="text-left">{c.name}</span>
                                                                </button>
                                                            ))
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Quiz Custom Dropdown */}
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-wide">Chọn Bài kiểm tra</label>
                                        <div className="relative custom-dropdown-container">
                                            <button
                                                className={`
                                                    w-full text-left appearance-none border rounded-xl px-4 py-3 
                                                    focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 
                                                    transition-all duration-200 flex justify-between items-center shadow-sm
                                                    ${!selectedClassId
                                                        ? 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 text-gray-400 cursor-not-allowed'
                                                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:shadow-md'
                                                    }
                                                    ${openDropdown === 'quiz' ? 'ring-2 ring-blue-500/50 border-blue-500' : ''}
                                                `}
                                                onClick={() => selectedClassId && setOpenDropdown(openDropdown === 'quiz' ? null : 'quiz')}
                                                disabled={!selectedClassId}
                                            >
                                                <span className={`flex-1 text-left font-medium ${selectedQuiz ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>
                                                    {selectedQuiz ? selectedQuiz.title : (!selectedClassId ? '-- Chọn lớp học trước --' : '-- Chọn bài kiểm tra --')}
                                                </span>
                                                <FaChevronDown className={`text-xs text-gray-400 transition-transform duration-300 ${openDropdown === 'quiz' ? 'rotate-180 text-blue-500' : ''}`} />
                                            </button>

                                            {openDropdown === 'quiz' && selectedClassId && (
                                                <div className="absolute top-full left-0 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-20 overflow-hidden animate-slideUp text-left">
                                                    <div className="max-h-60 overflow-y-auto custom-scrollbar">
                                                        {classQuizzes.length === 0 ? (
                                                            <div className="px-4 py-3 text-gray-500 text-sm">Lớp chưa có quiz nào</div>
                                                        ) : (
                                                            classQuizzes.map(q => (
                                                                <button
                                                                    key={q.id}
                                                                    onClick={() => {
                                                                        setSelectedQuizId(q.id);
                                                                        setOpenDropdown(null);
                                                                    }}
                                                                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${selectedQuizId === q.id ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-700 dark:text-gray-300'}`}
                                                                >
                                                                    <span className="text-left">{q.title}</span>
                                                                </button>
                                                            ))
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {quizDetails && (
                            <div className="space-y-8 animate-slideUpIn">
                                {/* Overview Cards */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {[
                                        { label: 'Tổng lượt làm bài', value: quizDetails.stats.totalAttempts, icon: FaClipboardList, color: 'blue' },
                                        { label: 'Điểm trung bình', value: `${(quizDetails.stats.averageScore || 0).toFixed(1)}`, icon: FaTrophy, color: 'green' },
                                        { label: 'Người tham gia', value: quizDetails.stats.uniqueParticipants, icon: FaUsers, color: 'purple' }
                                    ].map((item, idx) => (
                                        <div
                                            key={idx}
                                            className="
                                                relative bg-white border border-gray-200 rounded-2xl p-4 text-left
                                                transition-all duration-300 hover:shadow-xl hover:-translate-y-1
                                                dark:bg-gradient-to-br dark:from-slate-800 dark:to-slate-900
                                                dark:border-white/10 dark:ring-1 dark:ring-white/5
                                                overflow-hidden group isolate
                                                flex flex-row items-center justify-between gap-4
                                            "
                                            style={{ WebkitMaskImage: '-webkit-radial-gradient(white, white)' } as React.CSSProperties}
                                        >
                                            {/* Overlay pattern elements */}
                                            <div className="absolute inset-0 opacity-10 bg-[repeating-linear-gradient(135deg,_rgba(0,0,0,0.08)_0px,_rgba(0,0,0,0.08)_1px,_transparent_1px,_transparent_8px)] dark:bg-[repeating-linear-gradient(135deg,_rgba(255,255,255,0.15)_0px,_rgba(255,255,255,0.15)_1px,_transparent_1px,_transparent_8px)] rounded-xl pointer-events-none" />
                                            <div className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity duration-1000 bg-gradient-to-r from-transparent via-white/80 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] blur-[2px] animate-[shimmer_1.8s_ease-in-out_infinite] rounded-xl mix-blend-overlay pointer-events-none" />

                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 rounded-2xl bg-${item.color}-50 dark:bg-white/10 flex items-center justify-center text-${item.color}-600 dark:text-${item.color}-400 shadow-sm group-hover:scale-110 transition-transform duration-300 flex-shrink-0`}>
                                                    <item.icon className="text-lg" />
                                                </div>
                                                <h3 className="text-gray-500 dark:text-gray-400 text-xs font-bold uppercase tracking-wider">{item.label}</h3>
                                            </div>

                                            <div>
                                                <p className="text-xl font-mono font-bold text-gray-900 dark:text-white leading-none text-right">{item.value}</p>
                                                <p className="text-xl font-mono font-bold text-gray-900 dark:text-gray-200 leading-none text-right">{item.value}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Results Table */}
                                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
                                    <div className="p-6 relative flex justify-between items-center">
                                        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent"></div>
                                        <h3 className="text-lg font-bold dark:text-gray-200 flex items-center gap-2">
                                            <FaClipboardList className="text-blue-500" /> Kết quả chi tiết
                                            <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full text-xs">{quizDetails.sessions.length}</span>
                                        </h3>
                                    </div>

                                    {quizDetails.sessions.length === 0 ? (
                                        <div className="p-12 text-center text-gray-500">
                                            Chưa có dữ liệu bài làm nào.
                                        </div>
                                    ) : (
                                        <>
                                            {/* Desktop Table View */}
                                            <div className="hidden md:block overflow-x-auto max-h-[500px] overflow-y-auto custom-scrollbar">
                                                <table className="w-full text-left border-collapse">
                                                    <thead className="bg-gray-50/50 dark:bg-gray-800/50 backdrop-blur-sm text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider font-semibold relative">
                                                        <tr>
                                                            <th className="py-4 px-6">Thành viên</th>
                                                            <th className="py-4 px-6">Điểm số</th>
                                                            <th className="py-4 px-6">Thời gian</th>
                                                            <th className="py-4 px-6 text-right">Thao tác</th>
                                                        </tr>
                                                        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent"></div>
                                                    </thead>
                                                    <tbody>
                                                        {quizDetails.sessions.map((s: any) => (
                                                            <tr key={s.id} className="group hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors duration-200 relative gradient-row-divider">
                                                                <td className="py-4 px-6 align-middle">
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold shadow-md">
                                                                            {(s.userName || s.email || '?').charAt(0).toUpperCase()}
                                                                        </div>
                                                                        <span className="font-medium text-gray-900 dark:text-gray-200">{s.userName || s.email || 'Unknown'}</span>
                                                                    </div>
                                                                </td>
                                                                <td className="py-4 px-6 align-middle">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="font-medium text-gray-900 dark:text-gray-200 text-lg">{s.score}/{s.totalQuestions}</span>
                                                                        <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-sm ${(s.score / s.totalQuestions) >= 0.8 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                                                            (s.score / s.totalQuestions) >= 0.5 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                                                                'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                                                            }`}>
                                                                            {Math.round((s.score / s.totalQuestions) * 100)}%
                                                                        </span>
                                                                        <div className="relative h-1.5 w-16 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                                                            <div
                                                                                className={`absolute top-0 left-0 h-full rounded-full ${(s.score / s.totalQuestions) >= 0.8 ? 'bg-green-500' :
                                                                                    (s.score / s.totalQuestions) >= 0.5 ? 'bg-yellow-500' : 'bg-red-500'
                                                                                    }`}
                                                                                style={{ width: `${(s.score / s.totalQuestions) * 100}%` }}
                                                                            ></div>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td className="py-4 px-6 align-middle text-sm text-gray-500 font-mono">
                                                                    {formatDateTime(s.completedAt)}
                                                                </td>
                                                                <td className="py-4 px-6 align-middle text-right">
                                                                    <button
                                                                        onClick={() => navigate(`/results/${quizDetails.quizId || selectedQuizId}`, {
                                                                            state: {
                                                                                sessionId: s.id,
                                                                                fromProfile: true,
                                                                                activeTab: 'management',
                                                                                selectedClassId,
                                                                                selectedQuizId
                                                                            }
                                                                        })}
                                                                        className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 transition-all shadow-sm hover:shadow-md"
                                                                    >
                                                                        Chi tiết
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {/* Mobile Card View */}
                                            <div className="md:hidden p-4 space-y-4 max-h-[500px] overflow-y-auto custom-scrollbar">
                                                {quizDetails.sessions.map((s: any) => (
                                                    <div key={s.id} className="bg-gray-100 dark:bg-gray-700 rounded-xl p-4 space-y-3 relative gradient-row-divider border border-gray-200 dark:border-gray-700">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-sm font-bold shadow-md flex-shrink-0">
                                                                {(s.userName || s.email || '?').charAt(0).toUpperCase()}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="font-bold text-gray-900 dark:text-gray-200 truncate">{s.userName}</div>
                                                                <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">{formatDateTime(s.completedAt)}</div>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold mb-1">Điểm số</div>
                                                                <div className="flex items-center gap-2">
                                                                    <div className="flex items-baseline gap-1">
                                                                        <span className="font-bold text-2xl text-gray-900 dark:text-white">{s.score}</span>
                                                                        <span className="text-gray-400 text-base">/{s.totalQuestions}</span>
                                                                    </div>
                                                                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold shadow-sm ${(s.score / s.totalQuestions) >= 0.8 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                                                        (s.score / s.totalQuestions) >= 0.5 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                                                            'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                                                        }`}>
                                                                        {Math.round((s.score / s.totalQuestions) * 100)}%
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div className="relative h-2 w-24 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                                                                <div
                                                                    className={`absolute top-0 left-0 h-full rounded-full ${(s.score / s.totalQuestions) >= 0.8 ? 'bg-green-500' :
                                                                        (s.score / s.totalQuestions) >= 0.5 ? 'bg-yellow-500' : 'bg-red-500'
                                                                        }`}
                                                                    style={{ width: `${(s.score / s.totalQuestions) * 100}%` }}
                                                                ></div>
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={() => navigate(`/results/${quizDetails.quizId || selectedQuizId}`, {
                                                                state: {
                                                                    sessionId: s.id,
                                                                    fromProfile: true,
                                                                    activeTab: 'management',
                                                                    selectedClassId,
                                                                    selectedQuizId
                                                                }
                                                            })}
                                                            className="w-full py-2.5 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 transition-all shadow-sm active:scale-95"
                                                        >
                                                            Xem chi tiết
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Access List */}
                                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
                                    <div className="p-6 relative">
                                        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent"></div>
                                        <h3 className="text-lg font-bold dark:text-white flex items-center gap-2">
                                            <FaUsers className="text-blue-500" /> Danh sách quyền truy cập
                                        </h3>
                                        <p className="text-sm text-gray-500 mt-1">Những người dùng được cấp quyền truy cập riêng tư</p>
                                    </div>
                                    {/* Desktop Table View */}
                                    <div className="hidden md:block overflow-x-auto max-h-[400px] overflow-y-auto custom-scrollbar">
                                        <table className="w-full text-left border-collapse">
                                            <thead className="bg-gray-50/50 dark:bg-gray-800/50 backdrop-blur-sm text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider font-semibold relative">
                                                <tr>
                                                    <th className="py-4 px-6">Người dùng</th>
                                                    <th className="py-4 px-6">Quyền hạn</th>
                                                    <th className="py-4 px-6">Ngày tham gia</th>
                                                </tr>
                                                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent"></div>
                                            </thead>
                                            <tbody>
                                                {quizDetails.accessList && quizDetails.accessList.length > 0 ? (
                                                    quizDetails.accessList.map((a: any) => (
                                                        <tr key={a.id} className="hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors relative gradient-row-divider">
                                                            <td className="py-4 px-6">
                                                                <div className="font-medium text-gray-900 dark:text-white">{a.name}</div>
                                                            </td>
                                                            <td className="py-4 px-6">
                                                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${a.accessLevel === 'full'
                                                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                                                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                                                    }`}>
                                                                    {a.accessLevel === 'full' ? 'Xem & Làm bài' : 'Chỉ xem'}
                                                                </span>
                                                            </td>
                                                            <td className="py-4 px-6 text-sm text-gray-600 dark:text-gray-300 font-mono">
                                                                {formatDateTime(a.joinedAt)}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={3} className="py-8 text-center text-gray-500 italic">Chưa có thành viên nào trong danh sách truy cập.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile Card View */}
                                    <div className="md:hidden p-4 space-y-4 max-h-[400px] overflow-y-auto custom-scrollbar">
                                        {quizDetails.accessList && quizDetails.accessList.length > 0 ? (
                                            quizDetails.accessList.map((a: any) => (
                                                <div key={a.id} className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 space-y-3 relative gradient-row-divider">
                                                    <div>
                                                        <div className="font-bold text-gray-900 dark:text-white">{a.name}</div>
                                                    </div>
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold mb-1">Quyền hạn</div>
                                                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${a.accessLevel === 'full'
                                                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                                                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                                                }`}>
                                                                {a.accessLevel === 'full' ? 'Xem & Làm bài' : 'Chỉ xem'}
                                                            </span>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold mb-1">Tham gia</div>
                                                            <div className="text-sm text-gray-600 dark:text-gray-300 font-mono">{formatDateTime(a.joinedAt)}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="py-8 text-center text-gray-500 italic">Chưa có thành viên nào trong danh sách truy cập.</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProfilePage;
